import React, { useMemo, useState } from 'react';
import { DropdownMenu, Text, SegmentedControl } from '@spark';
import ConfusionMatrixHeatmap from './ConfusionMatrixHeatmap';
import ConfusionMatrixLegend from './ConfusionMatrixLegend';
import { ALL_LABELS } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import {
  getTopLabelIndicesBySupport,
  normalizeLabelConfusionPayload,
  sliceLabelsAndMatrix,
  sliceMatrixToActualLabel,
  sliceMatrixToPredictedLabel,
} from './confusionMatrixUtils';
import './ConfusionMatrixComparison.css';

const GREEN_SHADES = [
  'var(--primitive-green-200)',
  'var(--primitive-green-300)',
  'var(--primitive-green-400)',
];

const RED_SHADES = [
  'var(--primitive-red-200)',
  'var(--primitive-red-300)',
  'var(--primitive-red-400)',
];

const PALETTE = {
  diagonalShades: GREEN_SHADES,
  offDiagonalShades: RED_SHADES,
  diagonalSolid: GREEN_SHADES[GREEN_SHADES.length - 1],
  offDiagonalSolid: RED_SHADES[RED_SHADES.length - 1],
  zeroSolid: 'var(--primitive-neutral-200)',
};

const SHOW_SIZE_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Top 10', value: 10 },
  { label: 'Top 20', value: 20 },
];

const SHOW_SIZE_ITEMS = SHOW_SIZE_OPTIONS.map(o => ({ label: o.label }));

export default function ConfusionMatrixComparison({
  payload,
  showComparison,
  oldModelTitle,
  newModelTitle,
  selectedLabel,
}) {
  const normalized = useMemo(() => normalizeLabelConfusionPayload(payload), [payload]);
  const hasOld = showComparison && normalized?.old?.matrix && normalized?.new?.matrix;

  const [modelKey, setModelKey] = useState('new');
  const [showSize, setShowSize] = useState('all');

  const modelAName = modelKey === 'old' ? (oldModelTitle || 'Teacher model') : (newModelTitle || 'SpecificAI');
  const modelBName = modelKey === 'old' ? (newModelTitle || 'SpecificAI') : (oldModelTitle || 'Teacher model');

  const baseMatrixForTop = useMemo(() => {
    if (hasOld) return normalized?.new?.matrix || normalized?.old?.matrix || null;
    return normalized?.new?.matrix || null;
  }, [hasOld, normalized?.new?.matrix, normalized?.old?.matrix]);

  const topIndices = useMemo(() => {
    if (!baseMatrixForTop) return null;
    if (showSize === 'all') return null;
    return getTopLabelIndicesBySupport(baseMatrixForTop, Number(showSize));
  }, [baseMatrixForTop, showSize]);

  const focusActualLabel = selectedLabel && selectedLabel !== ALL_LABELS;

  const { actualView, predictedView } = useMemo(() => {
    const empty = { labels: [], columnLabels: [], matrix: null };
    if (!normalized?.labels?.length) return { actualView: empty, predictedView: null };

    const toSquare = (labs, mat) => {
      if (!mat) return { labels: labs, columnLabels: labs, matrix: null };
      if (!topIndices) return { labels: labs, columnLabels: labs, matrix: mat };
      const sliced = sliceLabelsAndMatrix(labs, mat, topIndices);
      return { labels: sliced.labels, columnLabels: sliced.labels, matrix: sliced.matrix };
    };

    const sourceMatrix = !hasOld
      ? normalized?.new?.matrix
      : (modelKey === 'old' ? normalized?.old?.matrix : normalized?.new?.matrix);

    if (focusActualLabel) {
      if (!sourceMatrix) return { actualView: empty, predictedView: empty };
      const actual = sliceMatrixToActualLabel(normalized.labels, sourceMatrix, selectedLabel);
      const predicted = sliceMatrixToPredictedLabel(normalized.labels, sourceMatrix, selectedLabel);
      return {
        actualView: { labels: actual.rowLabels, columnLabels: actual.colLabels, matrix: actual.matrix },
        predictedView: { labels: predicted.rowLabels, columnLabels: predicted.colLabels, matrix: predicted.matrix },
      };
    }

    return { actualView: toSquare(normalized.labels, sourceMatrix), predictedView: null };
  }, [focusActualLabel, hasOld, modelKey, normalized, selectedLabel, topIndices]);

  const showSizeDisplayText = useMemo(() => {
    const found = SHOW_SIZE_OPTIONS.find(o => o.value === showSize);
    return found ? found.label : 'All';
  }, [showSize]);

  const content = (
    <ConfusionMatrixHeatmap
      labels={actualView.labels}
      columnLabels={actualView.columnLabels}
      matrix={actualView.matrix}
      palette={PALETTE}
      tooltipModelA={modelAName}
      rowAxisLabel="Actual"
      colAxisLabel="Predicted"
    />
  );

  const predictedContent = predictedView ? (
    <ConfusionMatrixHeatmap
      labels={predictedView.labels}
      columnLabels={predictedView.columnLabels}
      matrix={predictedView.matrix}
      palette={PALETTE}
      tooltipModelA={modelAName}
      rowAxisLabel="Predicted"
      colAxisLabel="Actual"
    />
  ) : null;

  const showSizeControl = !focusActualLabel;
  const showControls = hasOld || showSizeControl;

  return (
    <div style={{ width: '100%' }}>
      {showControls && (
        <div className="cmControlsRow">
          {hasOld && (
            <div className="cmControlsGroup">
              <Text variant="detail-regular" color="secondary" as="span">Model:</Text>
              <SegmentedControl
                items={[
                  { id: 'old', label: oldModelTitle || 'Teacher model' },
                  { id: 'new', label: newModelTitle || 'SpecificAI' },
                ]}
                activeId={modelKey}
                onChange={setModelKey}
                size="small"
              />
            </div>
          )}

          {showSizeControl && (
            <div className="cmControlsGroup" style={{ marginLeft: 'auto' }}>
              <Text variant="detail-regular" color="secondary">Show:</Text>
              <DropdownMenu
                size="small"
                showLabel={false}
                placeholder="All"
                text={showSizeDisplayText}
                items={SHOW_SIZE_ITEMS.map((item, idx) => ({
                  ...item,
                  state: SHOW_SIZE_OPTIONS[idx].value === showSize ? 'selected' : 'default',
                }))}
                value={showSizeDisplayText}
                onSelect={(item, index) => {
                  setShowSize(SHOW_SIZE_OPTIONS[index].value);
                }}
              />
            </div>
          )}
        </div>
      )}

      {predictedContent ? (
        <div className="cm-focused-stack">
          {content}
          {predictedContent}
        </div>
      ) : (
        content
      )}

      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
          <Text variant="detail-regular" color="secondary">Legend:</Text>
        </div>
        <ConfusionMatrixLegend palette={PALETTE} />
      </div>
    </div>
  );
}
